import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  Autocomplete,
  Alert,
  Divider,
  Stack,
} from "@mui/material";
import { FilterList, Clear, Tune } from "@mui/icons-material";
import { useFuelReports } from "../../contexts/FuelReportsContext";
import {
  commonTextFieldProps,
  commonDateFieldProps,
  commonButtonProps,
  commonTypographyProps,
} from "../../utils/styles";
import {
  buildMonthOnMonthMenuOptions,
  buildQuarterMenuOptions,
  DEFAULT_VEHICLE_REPORT_DATA_YEAR,
  fuelReportMonthKey,
  fuelReportPeriodSelectValue,
  fuelReportQuarterCalendarRange,
  parseQuarterPresetValue,
} from "../../utils/fuelReportPeriodUtils";
import { formatToLocalYMD } from "../../utils/utils";

type PeriodMenuOption = { value: string; label: string; group: string };

function buildMonthQuarterPeriodOptions(year: number): PeriodMenuOption[] {
  return [
    ...buildMonthOnMonthMenuOptions(year).map((o) => ({
      value: o.value,
      label: o.label,
      group: `Month on month (${year})`,
    })),
    ...buildQuarterMenuOptions(year).map((o) => ({
      value: o.value,
      label: o.label,
      group: `Quarter on quarter (${year})`,
    })),
    {
      value: "__custom__",
      label: "Custom range (28+ days)",
      group: "Other",
    },
  ];
}

const MONTH_COUNT_CHOICES = [3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => ({
  value: n,
  label: `${n} months`,
}));

const VEHICLE_AGE_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "All ages" },
  { value: "new", label: "New (0-2 years)" },
  { value: "medium", label: "Medium (3-5 years)" },
  { value: "old", label: "Old (6-10 years)" },
  { value: "very_old", label: "Very old (10+ years)" },
];

interface FuelReportsFiltersProps {
  showMonthFilter?: boolean;
  onApplyFilters?: () => void;
  /**
   * `standard`: rolling presets (this month, last N months, this year, custom).
   * `monthQuarter`: calendar months as `YYYY-MM` and calendar quarters (custom range on API), aligned with `dataYear`.
   */
  periodPresetMode?: "standard" | "monthQuarter";
  /** Used with `monthQuarter` (default 2025). */
  dataYear?: number;
}

const FuelReportsFilters: React.FC<FuelReportsFiltersProps> = ({
  showMonthFilter = false,
  onApplyFilters,
  periodPresetMode = "standard",
  dataYear = DEFAULT_VEHICLE_REPORT_DATA_YEAR,
}) => {
  const {
    filters,
    monthOnMonthFilters,
    setFilters,
    setMonthOnMonthFilters,
    resetFilters,
    filterOptions,
    isLoadingFilterOptions,
    dateFilterPresets,
    isValidDateRange,
  } = useFuelReports();

  const defaultPeriodValue =
    periodPresetMode === "monthQuarter"
      ? fuelReportMonthKey(dataYear, 1)
      : "lastMonth";

  const handleDateFilterChange = (value: string) => {
    setFilters({ dateFilter: value });
    if (value !== "custom") {
      setFilters({ startDate: "", endDate: "" });
    }
  };

  const handleMonthQuarterPeriodChange = (value: string) => {
    if (value === "__custom__") {
      setFilters({ dateFilter: "custom", startDate: "", endDate: "" });
      return;
    }
    const q = parseQuarterPresetValue(value);
    if (q) {
      const { start, end } = fuelReportQuarterCalendarRange(q.year, q.quarter);
      setFilters({
        dateFilter: "custom",
        startDate: formatToLocalYMD(start),
        endDate: formatToLocalYMD(end),
      });
      return;
    }
    setFilters({ dateFilter: value, startDate: "", endDate: "" });
  };

  const handleMultiSelectChange = (field: string, values: string[]) => {
    setFilters({ [field]: values.join(",") });
  };

  const handleMonthsCountChange = (value: number) => {
    setMonthOnMonthFilters({ monthsCount: value });
  };

  const getSelectedValues = (field: string): string[] => {
    const value = filters[field as keyof typeof filters] as string;
    return value ? value.split(",").filter(Boolean) : [];
  };

  const hasActiveFilters = () => {
    const {
      dateFilter,
      vehicleModels,
      vehicleTypes,
      vehicleAge,
      districts,
      zones,
      regions,
      staffTypes,
      vehicleNumbers,
    } = filters;

    const periodOffBaseline =
      periodPresetMode === "monthQuarter"
        ? dateFilter !== fuelReportMonthKey(dataYear, 1) ||
          dateFilter === "custom"
        : dateFilter !== "lastMonth";

    return (
      periodOffBaseline ||
      vehicleModels ||
      vehicleTypes ||
      vehicleAge ||
      districts ||
      zones ||
      regions ||
      staffTypes ||
      vehicleNumbers
    );
  };

  const isCustomDateRangeValid = () => {
    if (filters.dateFilter !== "custom") return true;
    return isValidDateRange(filters.startDate, filters.endDate);
  };

  const monthQuarterPeriodOptions = useMemo(
    () => buildMonthQuarterPeriodOptions(dataYear),
    [dataYear]
  );

  const selectedMonthQuarterOption = useMemo(() => {
    const key = fuelReportPeriodSelectValue(filters, dataYear);
    return (
      monthQuarterPeriodOptions.find((o) => o.value === key) ??
      monthQuarterPeriodOptions[0]
    );
  }, [
    filters.dateFilter,
    filters.startDate,
    filters.endDate,
    dataYear,
    monthQuarterPeriodOptions,
  ]);

  const selectedStandardPeriod = useMemo(() => {
    const v = filters.dateFilter || "lastMonth";
    return (
      dateFilterPresets.find((p) => p.value === v) ?? dateFilterPresets[1]
    );
  }, [filters.dateFilter, dateFilterPresets]);

  const handleApplyFilters = () => {
    if (onApplyFilters) {
      onApplyFilters();
    }
  };

  const renderFilterChips = () => {
    const chips: JSX.Element[] = [];

    const showPeriodChip =
      periodPresetMode === "monthQuarter"
        ? filters.dateFilter !== fuelReportMonthKey(dataYear, 1) ||
          filters.dateFilter === "custom"
        : Boolean(filters.dateFilter && filters.dateFilter !== "lastMonth");

    if (showPeriodChip) {
      let periodLabel = filters.dateFilter || "";
      if (periodPresetMode === "monthQuarter") {
        if (/^\d{4}-\d{2}$/.test(filters.dateFilter || "")) {
          periodLabel = `Month ${filters.dateFilter}`;
        } else if (
          filters.dateFilter === "custom" &&
          filters.startDate &&
          filters.endDate
        ) {
          const sel = fuelReportPeriodSelectValue(filters, dataYear);
          const pq = parseQuarterPresetValue(sel);
          periodLabel = pq
            ? `Q${pq.quarter} ${pq.year} (${filters.startDate} - ${filters.endDate})`
            : `Custom (${filters.startDate} - ${filters.endDate})`;
        } else if (filters.dateFilter === "custom") {
          periodLabel = "Custom range (set dates)";
        }
      } else {
        const preset = dateFilterPresets.find(
          (p) => p.value === filters.dateFilter
        );
        periodLabel = preset?.label || filters.dateFilter || "";
      }
      chips.push(
        <Chip
          key="dateFilter"
          label={`Period: ${periodLabel}`}
          size="small"
          onDelete={() =>
            periodPresetMode === "monthQuarter"
              ? setFilters({
                  dateFilter: fuelReportMonthKey(dataYear, 1),
                  startDate: "",
                  endDate: "",
                })
              : handleDateFilterChange("lastMonth")
          }
        />
      );
    }

    Object.entries(filters).forEach(([key, value]) => {
      if (
        value &&
        key !== "dateFilter" &&
        key !== "startDate" &&
        key !== "endDate"
      ) {
        const values = (value as string).split(",");
        if (values.length > 0) {
          chips.push(
            <Chip
              key={key}
              label={`${key}: ${values.length} selected`}
              size="small"
              onDelete={() => setFilters({ [key]: "" })}
            />
          );
        }
      }
    });

    return chips;
  };

  /** One dense row on md+ (4×3 grid); full width on small screens. */
  const fieldGrid = { xs: 12, sm: 6, md: 3 } as const;

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1,
        borderColor: "divider",
        borderRadius: 1.5,
        overflow: "visible",
      }}
    >
      <CardContent sx={{ p: { xs: 1, sm: 1.25 }, py: 1.25, "&:last-child": { pb: 1.25 } }}>
        {/* Compact toolbar */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: hasActiveFilters() ? 0.75 : 1, flexWrap: "wrap", gap: 0.75 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
            <Tune sx={{ fontSize: "1.1rem", color: "primary.main", flexShrink: 0 }} />
            <Typography
              component="span"
              sx={{ fontWeight: 700, fontSize: "0.8rem", lineHeight: 1.2 }}
            >
              Filters
            </Typography>
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{
                display: { xs: "none", md: "inline" },
                fontSize: "0.65rem",
                ml: 0.5,
                borderLeft: "1px solid",
                borderColor: "divider",
                pl: 1,
              }}
            >
              Then Apply
            </Typography>
          </Box>

          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {hasActiveFilters() && (
              <Button
                onClick={resetFilters}
                startIcon={<Clear sx={{ fontSize: "1rem !important" }} />}
                color="inherit"
                {...commonButtonProps}
                sx={{ ...commonButtonProps.sx, color: "text.secondary", py: 0.25 }}
              >
                Clear
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleApplyFilters}
              disabled={!isCustomDateRangeValid()}
              startIcon={<FilterList sx={{ fontSize: "1rem !important" }} />}
              {...commonButtonProps}
              sx={{ ...commonButtonProps.sx, py: 0.25 }}
            >
              Apply
            </Button>
          </Stack>
        </Stack>

        {hasActiveFilters() && (
          <Box
            sx={{
              mb: 1,
              py: 0.5,
              px: 0.75,
              borderRadius: 1,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "divider",
              maxWidth: "100%",
              overflowX: "auto",
              overflowY: "hidden",
              "&::-webkit-scrollbar": { height: 6 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "nowrap",
                gap: 0.5,
                alignItems: "center",
                minHeight: 28,
              }}
            >
              {renderFilterChips()}
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 1 }} />

        {/* Single flowing grid; no section stacks (saves vertical space) */}
        <Grid container spacing={1}>
            <Grid item {...fieldGrid}>
              {periodPresetMode === "monthQuarter" ? (
                <Autocomplete
                  size="small"
                  options={monthQuarterPeriodOptions}
                  groupBy={(opt) => opt.group}
                  getOptionLabel={(opt) => opt.label}
                  isOptionEqualToValue={(a, b) => a.value === b.value}
                  value={selectedMonthQuarterOption}
                  onChange={(_, opt) => {
                    if (opt) handleMonthQuarterPeriodChange(opt.value);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Period" {...commonTextFieldProps} />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography sx={{ fontSize: "0.75rem" }}>
                        {option.label}
                      </Typography>
                    </li>
                  )}
                />
              ) : (
                <Autocomplete
                  size="small"
                  options={dateFilterPresets}
                  getOptionLabel={(opt) => opt.label}
                  isOptionEqualToValue={(a, b) => a.value === b.value}
                  value={selectedStandardPeriod}
                  onChange={(_, opt) => {
                    if (opt) handleDateFilterChange(opt.value);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Period" {...commonTextFieldProps} />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography sx={{ fontSize: "0.75rem" }}>
                        {option.label}
                      </Typography>
                    </li>
                  )}
                />
              )}
            </Grid>

            {filters.dateFilter === "custom" && (
              <>
                <Grid item {...fieldGrid}>
                  <TextField
                    {...commonDateFieldProps}
                    label="Start Date"
                    type="date"
                    value={filters.startDate || ""}
                    onChange={(e) => setFilters({ startDate: e.target.value })}
                    fullWidth
                  />
                </Grid>
                <Grid item {...fieldGrid}>
                  <TextField
                    {...commonDateFieldProps}
                    label="End Date"
                    type="date"
                    value={filters.endDate || ""}
                    onChange={(e) => setFilters({ endDate: e.target.value })}
                    fullWidth
                  />
                </Grid>
              </>
            )}

            {showMonthFilter && (
              <Grid item {...fieldGrid}>
                <Autocomplete
                  size="small"
                  options={MONTH_COUNT_CHOICES}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(a, b) => a.value === b.value}
                  value={
                    MONTH_COUNT_CHOICES.find(
                      (o) => o.value === (monthOnMonthFilters.monthsCount || 6)
                    ) ?? MONTH_COUNT_CHOICES[3]
                  }
                  onChange={(_, opt) =>
                    handleMonthsCountChange(opt?.value ?? 6)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Months to analyze"
                      {...commonTextFieldProps}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography sx={{ fontSize: "0.75rem" }}>
                        {option.label}
                      </Typography>
                    </li>
                  )}
                />
              </Grid>
            )}

            <Grid item {...fieldGrid}>
              <Autocomplete
                multiple
                size="small"
                options={filterOptions?.vehicle_models || []}
                value={getSelectedValues("vehicleModels")}
                onChange={(_, values) =>
                  handleMultiSelectChange("vehicleModels", values)
                }
                loading={isLoadingFilterOptions}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      size="small"
                      sx={{ ...commonTypographyProps }}
                      {...getTagProps({ index })}
                      key={option}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    {...commonTextFieldProps}
                    label="Vehicle Models"
                    placeholder="Select models"
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Typography sx={{ fontSize: "0.75rem" }}>
                      {option}
                    </Typography>
                  </li>
                )}
              />
            </Grid>

            <Grid item {...fieldGrid}>
              <Autocomplete
                multiple
                size="small"
                options={filterOptions?.vehicle_types || []}
                value={getSelectedValues("vehicleTypes")}
                onChange={(_, values) =>
                  handleMultiSelectChange("vehicleTypes", values)
                }
                loading={isLoadingFilterOptions}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      size="small"
                      sx={{ ...commonTypographyProps }}
                      {...getTagProps({ index })}
                      key={option}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    {...commonTextFieldProps}
                    label="Vehicle Types"
                    placeholder="Select types"
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Typography sx={{ fontSize: "0.75rem" }}>
                      {option}
                    </Typography>
                  </li>
                )}
              />
            </Grid>

            <Grid item {...fieldGrid}>
              <Autocomplete
                size="small"
                options={VEHICLE_AGE_OPTIONS}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                value={
                  VEHICLE_AGE_OPTIONS.find(
                    (o) => o.value === (filters.vehicleAge || "")
                  ) ?? VEHICLE_AGE_OPTIONS[0]
                }
                onChange={(_, opt) =>
                  setFilters({ vehicleAge: opt?.value ?? "" })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vehicle age"
                    {...commonTextFieldProps}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Typography sx={{ fontSize: "0.75rem" }}>
                      {option.label}
                    </Typography>
                  </li>
                )}
              />
            </Grid>

            <Grid item {...fieldGrid}>
              <Autocomplete
                multiple
                size="small"
                options={filterOptions?.regions || []}
                value={getSelectedValues("regions")}
                onChange={(_, values) =>
                  handleMultiSelectChange("regions", values)
                }
                loading={isLoadingFilterOptions}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      size="small"
                      sx={{ ...commonTypographyProps }}
                      {...getTagProps({ index })}
                      key={option}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    {...commonTextFieldProps}
                    label="Regions"
                    placeholder="Select regions"
                  />
                )}
              />
            </Grid>

            <Grid item {...fieldGrid}>
              <Autocomplete
                multiple
                size="small"
                options={filterOptions?.zones || []}
                value={getSelectedValues("zones")}
                onChange={(_, values) =>
                  handleMultiSelectChange("zones", values)
                }
                loading={isLoadingFilterOptions}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      size="small"
                      sx={{ ...commonTypographyProps }}
                      {...getTagProps({ index })}
                      key={option}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    {...commonTextFieldProps}
                    label="Zones"
                    placeholder="Select zones"
                  />
                )}
              />
            </Grid>

            <Grid item {...fieldGrid}>
              <Autocomplete
                multiple
                size="small"
                options={filterOptions?.districts || []}
                value={getSelectedValues("districts")}
                onChange={(_, values) =>
                  handleMultiSelectChange("districts", values)
                }
                loading={isLoadingFilterOptions}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      size="small"
                      sx={{ ...commonTypographyProps }}
                      {...getTagProps({ index })}
                      key={option}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    {...commonTextFieldProps}
                    label="Districts"
                    placeholder="Select districts"
                  />
                )}
              />
            </Grid>

            <Grid item {...fieldGrid}>
              <Autocomplete
                multiple
                size="small"
                options={filterOptions?.staff_types || []}
                value={getSelectedValues("staffTypes")}
                onChange={(_, values) =>
                  handleMultiSelectChange("staffTypes", values)
                }
                loading={isLoadingFilterOptions}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      size="small"
                      sx={{ ...commonTypographyProps }}
                      {...getTagProps({ index })}
                      key={option}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    {...commonTextFieldProps}
                    label="Staff Types"
                    placeholder="Select staff types"
                  />
                )}
              />
            </Grid>
        </Grid>

        {filters.dateFilter === "custom" && !isCustomDateRangeValid() && (
          <Alert
            severity="warning"
            variant="outlined"
            sx={{ mt: 1, py: 0.25, "& .MuiAlert-message": { py: 0.5 } }}
          >
            <Typography variant="caption" sx={{ ...commonTypographyProps }}>
              Custom range: at least 28 days between start and end.
            </Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default FuelReportsFilters;
